import { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Calendar } from "@/components/ui/calendar";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Calendar as CalendarIcon, Clock, Users, CheckCircle2, ArrowLeft, AlertCircle, Baby, XCircle, Phone, Mail } from "lucide-react";
import { StrollerIcon } from "@/components/icons/StrollerIcon";
import { PiktableLoader } from "@/components/PiktableLoader";
import { format, addDays, isAfter, isBefore, startOfDay } from "date-fns";
import { fr } from "date-fns/locale";
import { useSupabaseData } from "@/hooks/useSupabaseData";
import type { ConflictCheckOptions } from "@/lib/reservationConflictUtils";
import { canPersonCountBeServed } from "@/lib/tableAssignmentUtils";
import { calculateEffectiveCapacityFromTotal } from "@/lib/childrenCapacityUtils";
import { useEstablishmentBySlug } from "@/contexts/EstablishmentContext";
import { useReservationSettings } from "@/hooks/useReservationSettings";
import { useTimeSlotLimits, resolveEffectiveLimit } from "@/hooks/useTimeSlotLimits";
import { TimeBookingGrid } from "@/components/TimeBookingGrid";
import { generateAvailableTimes, doTimesOverlap, countReservationsByTime, countPeopleByTime, normalizeTimeFormat } from "@/lib/timeBookingUtils";
import { useTimeSlotClosures } from "@/hooks/useTimeSlotClosures";
import { useTimeSlotBlocksQuery } from "@/hooks/queries/useTimeSlotBlocksQuery";
import { filterBlockedTimes, durationForTime, slotLabelMinutes } from "../../supabase/functions/_shared/timeBlocks";
import { formatDateLocal } from "@/lib/timeFormatUtils";
import { findParentSlotId, getEffectiveDuration } from "@/lib/timeSlotUtils";
import { TerracePreferenceSelector } from "@/components/TerracePreferenceSelector";
import { ZonePreferenceSelector } from "@/components/ZonePreferenceSelector";
import { useBookableZones } from "@/hooks/useBookableZones";
import { useZoneTimeWindows } from "@/hooks/useZoneTimeWindows";
import { getSlotsForZonePreference } from "@/lib/zoneScheduleUtils";
import { useTerraceClosures } from "@/hooks/useTerraceClosures";
import { useEstablishmentClosures } from "@/hooks/useEstablishmentClosures";
import type { TerracePreference } from "@/types/table";
import { useEstablishmentNotices } from "@/hooks/useEstablishmentNotices";
import { selectApplicableNotice } from "@/lib/establishmentNoticeUtils";
import { EstablishmentNoticeAlert } from "@/components/EstablishmentNoticeAlert";
import { diagnoseBookingPlacement, diagnosePublicSlots, getPublicSlotsForPlacement, normalizePublicBookingParty, resolvePublicTerracePreference } from "@/lib/publicAvailability";
import { PlacementSelectionSummary } from "@/components/PlacementSelectionSummary";
import { usePublicPlacementSelection, useSelectedPlacementGuard } from "@/hooks/usePublicPlacementSelection";
import { isGroupAboveOnlineLimit, GROUP_MODIFICATION_MESSAGE, resolveGroupContact } from "@/lib/groupModificationLimit";

export default function ModifyReservation() {
  const { token, slug } = useParams<{ token: string; slug?: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();

  const { establishment, loading: establishmentLoading, error: establishmentError } = useEstablishmentBySlug(slug);

  const { 
    tables, 
    combinedTables, 
    reservations, 
    availabilities, 
    weekdayAvailabilities, 
    bookablePeriods,
    timeSlots: supabaseTimeSlots,
    dateOverrides,
    loading: dataLoading,
    refetch,
  } = useSupabaseData({
    slug: slug,
    establishmentId: establishment?.id
  });

  const { settings: reservationSettings, loading: settingsLoading } = useReservationSettings({ establishmentId: establishment?.id });
  const { limits: timeSlotLimits } = useTimeSlotLimits({ establishmentId: establishment?.id });
  const { isSlotClosedOnDate, getSlotClosureMessage } = useTimeSlotClosures({ establishmentId: establishment?.id });
  const { data: timeBlocks = [] } = useTimeSlotBlocksQuery(establishment?.id);
  const { isTerraceClosedOnDate } = useTerraceClosures({ establishmentId: establishment?.id });
  const { isDateInClosure } = useEstablishmentClosures({ establishmentId: establishment?.id });
  const { notices: establishmentNotices } = useEstablishmentNotices({
    establishmentId: establishment?.id,
    usePublicClient: true,
  });

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [reservation, setReservation] = useState<any>(null);
  const [blockReason, setBlockReason] = useState<{
    title: string;
    message: string;
    showPhone: boolean;
    showEmail: boolean;
    blockedActionsPhone: string | null;
    blockedActionsEmail: string | null;
  } | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const applicableNotice = useMemo(
    () => selectApplicableNotice(establishmentNotices, selectedDate),
    [establishmentNotices, selectedDate]
  );
  const [persons, setPersons] = useState<string>("");
  const [selectedSlot, setSelectedSlot] = useState<string>("");
  const [initialTimeLoaded, setInitialTimeLoaded] = useState(false);
  const [numberOfChildren, setNumberOfChildren] = useState<number>(0);
  const [hasStroller, setHasStroller] = useState(false);
  const [terracePreference, setTerracePreference] = useState<TerracePreference>('no_preference');
  const terraceChoiceMade = !reservationSettings?.terraceEnabled || terracePreference !== 'no_preference';
  const effectiveTerracePreference = resolvePublicTerracePreference(reservationSettings?.terraceEnabled, terracePreference);
  const [zonePreference, setZonePreference] = useState<string[]>([]);
  const { zones: bookableZones } = useBookableZones({
    establishmentId: establishment?.id,
    useAuthenticatedData: false,
  });
  const { zoneWindows } = useZoneTimeWindows({
    establishmentId: establishment?.id,
    useAuthenticatedData: false,
  });
  const activeZones = bookableZones.filter(z => z.isActive);
  const zonesUiEnabled = (reservationSettings?.zonesEnabled ?? false) && activeZones.length > 0;
  const activeZoneIds = useMemo(
    () => new Set(activeZones.map(z => z.id)),
    [activeZones]
  );

  // Créneaux effectifs : horaires propres à la salle si toutes les salles choisies en ont, sinon calcul historique
  const slotsFor = useCallback(
    (d: Date) => getSlotsForZonePreference(
      d,
      zonesUiEnabled ? zonePreference : [],
      supabaseTimeSlots,
      dateOverrides,
      zoneWindows,
      activeZoneIds,
    ),
    [zonesUiEnabled, zonePreference, supabaseTimeSlots, dateOverrides, zoneWindows, activeZoneIds]
  );
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [smsConsent, setSmsConsent] = useState(false);
  const clearSelectedSlot = useCallback(() => setSelectedSlot(""), []);
  const resetLoadedSlot = useCallback(() => setInitialTimeLoaded(false), []);
  const { changeTerracePreference, changeZonePreference } = usePublicPlacementSelection({
    setTerracePreference,
    setZonePreference,
    clearSelectedSlot,
    resetLoadedSlot,
  });

  // Vérifier le token et récupérer la réservation
  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        toast({
          title: "Erreur",
          description: "Token invalide",
          variant: "destructive",
        });
        return;
      }

      try {
        // Appeler l'edge function pour valider le token et récupérer la réservation
        const { data, error } = await supabase.functions.invoke("get-reservation-by-token", {
          body: { token, action: "modify" },
        });

        // Helper pour mapper un payload d'erreur en bloc affiché
        const applyBlock = (payload: { error?: string; errorType?: string; showPhone?: boolean; showEmail?: boolean; blockedActionsPhone?: string | null; blockedActionsEmail?: string | null } | null) => {
          const errorType = payload?.errorType;
          const message = payload?.error || "Ce lien n'est plus utilisable.";
          const showPhone = payload?.showPhone === true;
          const showEmail = payload?.showEmail === true;
          const blockedActionsPhone = payload?.blockedActionsPhone ?? null;
          const blockedActionsEmail = payload?.blockedActionsEmail ?? null;
          const base = { message, showPhone, showEmail, blockedActionsPhone, blockedActionsEmail };
          if (errorType === 'same_day_modification_blocked') {
            setBlockReason({ title: "Modification non disponible en ligne", ...base });
          } else if (errorType === 'already_cancelled') {
            setBlockReason({ title: "Réservation déjà annulée", ...base, showPhone: false, showEmail: false });
          } else if (errorType === 'expired_token' || errorType === 'invalid_token') {
            setBlockReason({ title: "Lien expiré ou invalide", ...base });
          } else {
            setBlockReason({ title: "Impossible de modifier votre réservation", ...base });
          }
        };

        if (error) {
          console.error("Token validation error:", error);
          // FunctionsHttpError : le corps JSON (avec errorType) est dans error.context
          let payload: { error?: string; errorType?: string } | null = null;
          try {
            const ctx = (error as any).context;
            if (ctx && typeof ctx.json === 'function') {
              payload = await ctx.json();
            } else if (ctx && typeof ctx.text === 'function') {
              const txt = await ctx.text();
              try { payload = JSON.parse(txt); } catch { payload = { error: txt }; }
            }
          } catch (e) {
            console.warn("Could not parse error context:", e);
          }
          applyBlock(payload);
          return;
        }

        if (!data?.success || !data?.reservation) {
          applyBlock(data ?? null);
          return;
        }

        setReservation(data.reservation);
        setSelectedDate(new Date(data.reservation.date));
        const adults = data.reservation.number_of_people || 0;
        const children = data.reservation.number_of_children || 0;
        setPersons((adults + children).toString());
        setNumberOfChildren(children);
        setHasStroller(data.reservation.has_stroller ?? false);
        setTerracePreference((data.reservation.terrace_preference as TerracePreference) || 'no_preference');
        const zonePref = (data.reservation as any).zone_preference;
        setZonePreference(Array.isArray(zonePref) ? zonePref : []);
      } catch (error) {
        console.error("Error validating token:", error);
        toast({
          title: "Erreur",
          description: "Une erreur est survenue",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    validateToken();
  }, [token, toast]);

  // Determine booking mode and create conflict options
  const bookingMode = reservationSettings?.bookingMode ?? 'slot';
  const bookingIntervalMinutes = (reservationSettings?.bookingIntervalMinutes ?? 15) as 15 | 30 | 60;
  const reservationDurationMinutes = reservationSettings?.reservationDurationMinutes ?? 120;

  const conflictOptions: ConflictCheckOptions | undefined = useMemo(() => {
    if (!reservationSettings) return undefined;
    return {
      bookingMode: reservationSettings.bookingMode as 'slot' | 'time',
      reservationDurationMinutes: reservationSettings.reservationDurationMinutes ?? 120,
      serviceSlots: selectedDate ? slotsFor(selectedDate) : [],
    };
  }, [reservationSettings, selectedDate, slotsFor]);

  // Reset slot ONLY when date changes (new day = new availability)
  useEffect(() => {
    setSelectedSlot("");
    setInitialTimeLoaded(false);
  }, [selectedDate]);

  // Initialize selectedSlot with the reservation's current time once data is loaded
  useEffect(() => {
    if (reservation && !initialTimeLoaded && supabaseTimeSlots.length > 0 && reservationSettings) {
      const startTime = reservation.start_time.substring(0, 5); // "HH:mm"
      
      if (bookingMode === 'time') {
        // In TIME mode, use the time directly
        setSelectedSlot(startTime);
      } else {
        // In SLOT mode, find the slot that starts at this time
        const matchingSlot = supabaseTimeSlots.find(ts => {
          const slotStart = ts.start.substring(0, 5);
          return slotStart === startTime;
        });
        if (matchingSlot) {
          setSelectedSlot(`${matchingSlot.start.substring(0, 5)} - ${matchingSlot.end.substring(0, 5)}`);
        }
      }
      setInitialTimeLoaded(true);
    }
  }, [reservation, initialTimeLoaded, supabaseTimeSlots, reservationSettings, bookingMode]);

  // Map time slots from database, filtering by day of week
  const allTimeSlots = useMemo(() => {
    if (!selectedDate) return [];
    
    const dateStr = formatDateLocal(selectedDate);
    const slotsForDate = slotsFor(selectedDate);
    
    const hasOverrides = dateOverrides.some(o => o.date === dateStr && o.isActive);
    const filteredSlots = hasOverrides
      ? slotsForDate
      : slotsForDate.filter(ts => {
          if (ts.id && isSlotClosedOnDate(ts.id, dateStr)) return false;
          return true;
        });
    
    const labels = filteredSlots.map(ts => `${ts.start} - ${ts.end}`);
    // Heures fermées manuellement (mode créneaux : le créneau commençant à l'heure fermée)
    if (bookingMode === 'time') return labels;
    return labels.filter(l => (reservation && reservation.date === dateStr && String(reservation.start_time).substring(0, 5) === l.split(' - ')[0].substring(0, 5)) || filterBlockedTimes(dateStr, [l.split(' - ')[0]], timeBlocks, () => slotLabelMinutes(l)).length > 0);
  }, [timeBlocks, bookingMode, reservation, slotsFor, dateOverrides, selectedDate, isSlotClosedOnDate]);

  // Resolved TimeSlot[] objects for the current date (used by getEffectiveDuration)
  const resolvedSlotsForDate = useMemo(() => {
    if (!selectedDate) return [];
    return slotsFor(selectedDate);
  }, [selectedDate, slotsFor]);

  // Compute closure message when all slots are closed for the selected date
  const slotClosureMessage = useMemo(() => {
    if (!selectedDate) return undefined;
    const dateStr = formatDateLocal(selectedDate);
    const slotsForDate = slotsFor(selectedDate);
    const hasOverrides = dateOverrides.some(o => o.date === dateStr && o.isActive);
    const hasOpenSlot = hasOverrides
      ? slotsForDate.length > 0
      : slotsForDate.some(ts => {
          if (ts.id && isSlotClosedOnDate(ts.id, dateStr)) return false;
          return true;
        });
    if (hasOpenSlot) return undefined;
    for (const ts of supabaseTimeSlots) {
      if (ts.id) {
        const msg = getSlotClosureMessage(ts.id, dateStr);
        if (msg) return msg;
      }
    }
    return undefined;
  }, [selectedDate, slotsFor, supabaseTimeSlots, dateOverrides, isSlotClosedOnDate, getSlotClosureMessage]);

  // For TIME mode: Generate available booking times
  const availableBookingTimes = useMemo(() => {
    if (bookingMode !== 'time' || !selectedDate) return [];
    
    const dateStr = formatDateLocal(selectedDate);
    const slotsForDate = slotsFor(selectedDate);
    const hasOverrides = dateOverrides.some(o => o.date === dateStr && o.isActive);
    const filteredSlots = hasOverrides
      ? slotsForDate
      : slotsForDate.filter(ts => {
          if (ts.id && isSlotClosedOnDate(ts.id, dateStr)) return false;
          return true;
        });
    
    const timeSlotsForGen = filteredSlots.map(ts => ({
      start: ts.start.substring(0, 5),
      end: ts.end.substring(0, 5),
      durationOverrideMinutes: ts.durationOverrideMinutes,
    }));
    
    const generated = generateAvailableTimes(timeSlotsForGen, bookingIntervalMinutes, reservationDurationMinutes);
    // Heures fermées manuellement ; l'heure actuelle du client reste proposée (le serveur l'accepte)
    const currentTime = reservation && reservation.date === dateStr ? String(reservation.start_time).substring(0, 5) : null;
    return generated.filter(t => t === currentTime || filterBlockedTimes(dateStr, [t], timeBlocks, (x) => durationForTime(x, timeSlotsForGen, reservationDurationMinutes)).length > 0);
  }, [bookingMode, selectedDate, slotsFor, dateOverrides, bookingIntervalMinutes, reservationDurationMinutes, timeBlocks, reservation]);

  // Shared SLOT diagnosis: same assignment engine as final confirmation.
  const timeSlotAvailability = useMemo(() => {
    if (!selectedDate || bookingMode === 'time') return [];
    const total = Number.parseInt(persons, 10);
    const party = normalizePublicBookingParty(Number.isFinite(total) ? total : 0, numberOfChildren, hasStroller, reservationSettings?.childrenEnabled ?? false, reservationSettings?.strollerEnabled ?? false);
    return diagnosePublicSlots({
      slots: allTimeSlots, date: selectedDate, party, childrenCapacityMode: reservationSettings?.childrenCapacityMode ?? 'full', terracePreference: effectiveTerracePreference, zonePreference: zonesUiEnabled ? zonePreference : [],
      tables, combinedTables, reservations, availabilities, weekdayAvailabilities, bookablePeriods,
      getConflictOptions: slot => ({ bookingMode: 'slot', reservationDurationMinutes: getEffectiveDuration(slot.split(' - ')[0], resolvedSlotsForDate, reservationDurationMinutes), excludeReservationId: reservation?.id, serviceSlots: resolvedSlotsForDate }),
      isTerraceClosed: (dateStr, slot) => isTerraceClosedOnDate(dateStr, findParentSlotId(slot, supabaseTimeSlots)),
      customAssignmentOrderEnabled: reservationSettings?.customAssignmentOrderEnabled ?? false,
    });
  }, [selectedDate, bookingMode, persons, numberOfChildren, hasStroller, reservationSettings, allTimeSlots, effectiveTerracePreference, zonesUiEnabled, zonePreference, tables, combinedTables, reservations, reservation, availabilities, weekdayAvailabilities, bookablePeriods, resolvedSlotsForDate, reservationDurationMinutes, isTerraceClosedOnDate, supabaseTimeSlots]);

  // Count reservations by time for limit checks
  const reservationCountsByTime = useMemo(() => {
    if (bookingMode !== 'time' || !selectedDate) return new Map<string, number>();
    return countReservationsByTime(
      reservations
        .filter(r => r.id !== reservation?.id)
        .map(r => ({ 
          date: r.date, 
          startTime: r.startTime, 
          status: r.status 
        })), 
      selectedDate
    );
  }, [reservations, selectedDate, bookingMode]);

  // Count people by time for limit checks
  const peopleCountsByTime = useMemo(() => {
    if (bookingMode !== 'time' || !selectedDate) return new Map<string, number>();
    return countPeopleByTime(
      reservations
        .filter(r => r.id !== reservation?.id)
        .map(r => ({ 
          date: r.date, 
          startTime: r.startTime, 
          status: r.status,
          numberOfPeople: r.numberOfPeople,
          numberOfChildren: r.numberOfChildren
        })), 
      selectedDate
    );
  }, [reservations, selectedDate, bookingMode]);

  // Shared TIME diagnosis: same assignment engine as SLOT and final confirmation.
  const timeAvailability = useMemo(() => {
    if (bookingMode !== 'time' || !selectedDate) return [];
    const total = Number.parseInt(persons, 10);
    const party = normalizePublicBookingParty(Number.isFinite(total) ? total : 0, numberOfChildren, hasStroller, reservationSettings?.childrenEnabled ?? false, reservationSettings?.strollerEnabled ?? false);
    const timesWithinLimit = availableBookingTimes.filter(time => {
      const normalizedTime = normalizeTimeFormat(time);
      const effectiveLimit = resolveEffectiveLimit(selectedDate.getDay(), findParentSlotId(time, supabaseTimeSlots), timeSlotLimits, reservationSettings, normalizedTime);
      if (!effectiveLimit) return true;
      if (effectiveLimit.type === 'reservations') return (reservationCountsByTime.get(normalizedTime) || 0) < effectiveLimit.value;
      return party.total === 0 || (peopleCountsByTime.get(normalizedTime) || 0) + party.total <= effectiveLimit.value;
    });
    return diagnosePublicSlots({
      slots: timesWithinLimit, date: selectedDate, party, childrenCapacityMode: reservationSettings?.childrenCapacityMode ?? 'full', terracePreference: effectiveTerracePreference, zonePreference: zonesUiEnabled ? zonePreference : [],
      tables, combinedTables, reservations, availabilities, weekdayAvailabilities, bookablePeriods,
      getConflictOptions: slot => ({ bookingMode: 'time', reservationDurationMinutes: getEffectiveDuration(slot, resolvedSlotsForDate, reservationDurationMinutes), excludeReservationId: reservation?.id, serviceSlots: resolvedSlotsForDate }),
      isTerraceClosed: (dateStr, slot) => isTerraceClosedOnDate(dateStr, findParentSlotId(slot, supabaseTimeSlots)),
      customAssignmentOrderEnabled: reservationSettings?.customAssignmentOrderEnabled ?? false,
    });
  }, [bookingMode, selectedDate, persons, numberOfChildren, hasStroller, reservationSettings, availableBookingTimes, reservationCountsByTime, peopleCountsByTime, supabaseTimeSlots, timeSlotLimits, effectiveTerracePreference, zonesUiEnabled, zonePreference, tables, combinedTables, reservations, reservation, availabilities, weekdayAvailabilities, bookablePeriods, resolvedSlotsForDate, reservationDurationMinutes, isTerraceClosedOnDate]);

  // The shared engine already applies the selected party and every table constraint.
  const filteredTimeSlots = timeSlotAvailability;

  const placementAvailability = useMemo(() => {
    if (!selectedDate || !persons) return {};
    const total = Number.parseInt(persons, 10);
    if (!Number.isFinite(total) || total < 1) return {};
    const party = normalizePublicBookingParty(
      total, numberOfChildren, hasStroller,
      reservationSettings?.childrenEnabled ?? false,
      reservationSettings?.strollerEnabled ?? false,
    );
    const options = [
      ...(reservationSettings?.terraceEnabled ? [
        { key: 'indoor', terracePreference: 'indoor' as const, zonePreference: [] },
        { key: 'terrace', terracePreference: 'terrace' as const, zonePreference: [] },
      ] : []),
      ...(zonesUiEnabled ? activeZones.map(zone => ({ key: zone.id, terracePreference: 'no_preference' as const, zonePreference: [zone.id] })) : []),
    ];
    return diagnoseBookingPlacement({
      date: selectedDate, options,
      getSlots: option => getPublicSlotsForPlacement({
        date: selectedDate, zonePreference: option.zonePreference, bookingMode,
        regularSlots: supabaseTimeSlots, dateOverrides, zoneWindows, activeZoneIds,
        isSlotClosedOnDate, timeBlocks, bookingIntervalMinutes,
        reservationDurationMinutes,
      }),
      adults: party.adults, numberOfChildren: party.children,
      childrenCapacityMode: (reservationSettings?.childrenCapacityMode ?? 'full') as 'full' | 'half' | 'none',
      hasStroller: party.hasStroller, tables, combinedTables,
      reservations: reservations.filter(item => item.id !== reservation?.id),
      availabilities, weekdayAvailabilities, bookablePeriods,
      getConflictOptions: slot => bookingMode === 'time' ? { bookingMode: 'time', reservationDurationMinutes: getEffectiveDuration(slot, resolvedSlotsForDate, reservationDurationMinutes), excludeReservationId: reservation?.id, serviceSlots: resolvedSlotsForDate } : { ...conflictOptions, excludeReservationId: reservation?.id },
      isTerraceClosed: (dateStr, slot) => isTerraceClosedOnDate(dateStr || formatDateLocal(selectedDate), findParentSlotId(slot, supabaseTimeSlots)),
      customAssignmentOrderEnabled: reservationSettings?.customAssignmentOrderEnabled ?? false,
    });
  }, [selectedDate, persons, numberOfChildren, hasStroller, reservationSettings, zonesUiEnabled, activeZones, supabaseTimeSlots, dateOverrides, zoneWindows, activeZoneIds, bookingMode, tables, combinedTables, reservations, reservation, availabilities, weekdayAvailabilities, bookablePeriods, resolvedSlotsForDate, reservationDurationMinutes, bookingIntervalMinutes, conflictOptions, isTerraceClosedOnDate, isSlotClosedOnDate, timeBlocks]);

  // Never rewrite the customer's placement: keep it, clear the time, hide the grid.
  // Only the selected time is cleared (not the "initial time loaded" flag) so the
  // reservation's old time is not re-applied while the placement is unavailable.
  const { selectedPlacementUnavailable } = useSelectedPlacementGuard({
    terracePreference, zonePreference, placementAvailability, clearTimeSelection: clearSelectedSlot,
  });
  const canShowTimes = terraceChoiceMade && !selectedPlacementUnavailable;

  // Calculate available person counts based on selected time slot AND reservation settings limits
  const availablePersonCounts = useMemo(() => {
    if (!selectedDate) return [];
    const childrenCapacityMode = (reservationSettings?.childrenCapacityMode ?? 'full') as 'full' | 'half' | 'none';

    // Choisir la source de données selon le mode de réservation
    const isTimeMode = bookingMode === 'time';
    let maxCapacity = 0;

    if (isTimeMode) {
      // En mode TIME, utiliser timeAvailability qui contient les heures individuelles avec maxCapacity
      let relevantTimes = timeAvailability;
      if (selectedSlot) {
        relevantTimes = timeAvailability.filter(t => t.time === selectedSlot);
      }
      maxCapacity = Math.max(...relevantTimes.map(t => t.maxCapacity), 0);
    } else {
      // En mode SLOT, utiliser timeSlotAvailability
      let relevantSlots = timeSlotAvailability;
      if (selectedSlot) {
        relevantSlots = timeSlotAvailability.filter(s => s.time === selectedSlot);
      }
      maxCapacity = Math.max(...relevantSlots.map(s => s.maxCapacity), 0);
    }

    if (maxCapacity === 0) return [];

    // Apply min/max limits from reservation settings
    const minPeople = reservationSettings?.minPeople ?? 1;
    const maxPeople = reservationSettings?.maxPeople ?? 20;

    // Use the smaller of maxCapacity and maxPeople as upper limit
    const effectiveMax = Math.min(maxCapacity, maxPeople);

    // Generate person counts within the allowed range
    if (minPeople > effectiveMax) return [];

    return Array.from({ length: effectiveMax - minPeople + 1 }, (_, i) => {
      const count = minPeople + i;
      let isAvailable = false;
      
      if (isTimeMode) {
        const relevantTimes = selectedSlot 
          ? timeAvailability.filter(t => t.time === selectedSlot)
          : timeAvailability;
        const effectiveOccupancy = calculateEffectiveCapacityFromTotal(count, numberOfChildren, childrenCapacityMode);
        isAvailable = relevantTimes.some(t => t.isAvailable && canPersonCountBeServed(count, t.singleCapacities, t.combinedRawCapacities, t.combinedEffectiveCapacities, t.singleMinCapacities, t.combinedMinCapacities, effectiveOccupancy));
      } else {
        const relevantSlots = selectedSlot 
          ? timeSlotAvailability.filter(s => s.time === selectedSlot)
          : timeSlotAvailability;
        const effectiveOccupancy = calculateEffectiveCapacityFromTotal(count, numberOfChildren, childrenCapacityMode);
        isAvailable = relevantSlots.some(s => s.hasAvailability && canPersonCountBeServed(count, s.singleCapacities, s.combinedRawCapacities, s.combinedEffectiveCapacities, s.singleMinCapacities, s.combinedMinCapacities, effectiveOccupancy));
      }
      
      return {
        count,
        isAvailable
      };
    });
  }, [selectedDate, selectedSlot, timeSlotAvailability, timeAvailability, bookingMode, reservationSettings?.minPeople, reservationSettings?.maxPeople, reservationSettings?.childrenCapacityMode, numberOfChildren]);

  // Reset persons if current value is no longer available
  useEffect(() => {
    if (persons && availablePersonCounts.length > 0) {
      const currentPersons = parseInt(persons);
      const isStillAvailable = availablePersonCounts.some(
        p => p.count === currentPersons && p.isAvailable
      );
      if (!isStillAvailable) {
        setPersons("");
      }
    }
  }, [availablePersonCounts, persons]);

  // Guard: clamp children if persons decreases
  useEffect(() => {
    const total = parseInt(persons) || 0;
    if (total > 0 && numberOfChildren >= total) {
      setNumberOfChildren(Math.max(0, total - 1));
    }
  }, [persons, numberOfChildren]);

  const handleSubmit = async () => {
    const hasTimeOrSlot = bookingMode === 'time' ? selectedSlot : selectedSlot;
    if (!selectedDate || !hasTimeOrSlot || !persons) {
      toast({
        title: "Erreur",
        description: "Veuillez sélectionner une date, un nombre de personnes et un créneau",
        variant: "destructive",
      });
      return;
    }

    // Placement explicite obligatoire : jamais de repli silencieux vers « sans préférence »
    if (!canShowTimes) {
      toast({
        title: "Placement requis",
        description: "Merci de choisir un placement disponible, puis un horaire.",
        variant: "destructive",
      });
      return;
    }

    setSubmitting(true);

    try {
      // For TIME mode, selectedSlot is already in HH:mm format
      // For SLOT mode, extract from "HH:mm - HH:mm" format
      const startTime = bookingMode === 'time' 
        ? selectedSlot 
        : selectedSlot.split(' - ')[0];

      const totalPersons = parseInt(persons);
      const party = normalizePublicBookingParty(
        totalPersons, numberOfChildren, hasStroller,
        reservationSettings?.childrenEnabled ?? false,
        reservationSettings?.strollerEnabled ?? false,
      );

      const { data, error } = await supabase.functions.invoke("update-reservation-public", {
        body: {
          token,
          newDate: format(selectedDate, "yyyy-MM-dd"),
          newStartTime: startTime,
          numberOfPeople: party.adults,
          numberOfChildren: party.children,
          hasStroller: party.hasStroller,
          terracePreference,
          zonePreference: zonesUiEnabled && zonePreference.length > 0 ? zonePreference : [],
          marketingConsent,
          smsConsent,
        },
      });

      if (error) throw error;

      setSuccess(true);
      toast({
        title: "Réservation modifiée",
        description: "Votre réservation a été mise à jour avec succès. Un email de confirmation vous a été envoyé.",
      });

      // Rediriger vers la page de réservation avec le slug si disponible
      const redirectPath = slug ? `/${slug}` : "/";
      setTimeout(() => {
        navigate(redirectPath);
      }, 3000);
    } catch (error: any) {
      console.error("Error updating reservation:", error);
      // Afficher le message précis du serveur quand il existe (ex. placement requis)
      let serverMessage: string | undefined;
      try {
        const body = await error?.context?.json?.();
        if (typeof body?.error === 'string') serverMessage = body.error;
      } catch { /* corps illisible : message générique */ }
      await refetch();
      setSelectedSlot("");
      toast({
        title: serverMessage ? "Modification impossible" : "Horaire indisponible",
        description: serverMessage ?? "Cet horaire vient peut-être d’être réservé. Les disponibilités ont été actualisées, veuillez choisir un autre horaire.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Afficher erreur si établissement non trouvé (seulement si slug fourni)
  if (slug && establishmentError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md text-center p-8">
          <AlertCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2">Établissement non trouvé</h2>
          <p className="text-muted-foreground">
            L'établissement "{slug}" n'existe pas ou n'est plus actif.
          </p>
        </Card>
      </div>
    );
  }

  if (loading || dataLoading || establishmentLoading || settingsLoading || !reservationSettings) {
    return <PiktableLoader message="Chargement de votre réservation..." />;
  }

  if (blockReason) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md text-center p-8">
          <XCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2">{blockReason.title}</h2>
          <p className="text-muted-foreground mb-6">{blockReason.message}</p>
          {(() => {
            const phone = blockReason.blockedActionsPhone || establishment?.phone || null;
            const email = blockReason.blockedActionsEmail || establishment?.email || null;
            const showPhone = blockReason.showPhone && !!phone;
            const showEmail = blockReason.showEmail && !!email;
            if (!showPhone && !showEmail) return null;
            return (
              <div className="space-y-2 text-sm">
                {showPhone && (
                  <div className="flex items-center justify-center gap-2">
                    <Phone className="w-4 h-4 text-muted-foreground" />
                    <a href={`tel:${phone}`} className="text-primary hover:underline">{phone}</a>
                  </div>
                )}
                {showEmail && (
                  <div className="flex items-center justify-center gap-2">
                    <Mail className="w-4 h-4 text-muted-foreground" />
                    <a href={`mailto:${email}`} className="text-primary hover:underline">{email}</a>
                  </div>
                )}
              </div>
            );
          })()}
          <Button
            variant="outline"
            onClick={() => navigate(slug ? `/${slug}` : "/")}
            className="mt-6"
          >
            Retour
          </Button>
        </Card>
      </div>
    );
  }

  if (!reservation) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Erreur</CardTitle>
            <CardDescription>Impossible de trouver votre réservation</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-6 w-6 text-green-500" />
              <CardTitle>Réservation modifiée</CardTitle>
            </div>
            <CardDescription>
              Votre réservation a été mise à jour avec succès. Vous allez être redirigé...
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  const backPath = slug ? `/${slug}` : "/";
  const groupLocked = isGroupAboveOnlineLimit(reservation.number_of_people, reservationSettings?.maxPeople);
  const { phone, email } = resolveGroupContact(reservationSettings, establishment);

  return (
    <div className="min-h-screen bg-background py-12">
      <div className="container mx-auto px-4 max-w-4xl">
        <Button
          variant="ghost"
          onClick={() => navigate(backPath)}
          className="mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour
        </Button>

        <div className="mb-8">
          <h1 className="text-4xl md:text-5xl font-serif font-bold mb-4">Modifier ma réservation</h1>
          <p className="text-muted-foreground text-lg">
            Réservation pour {reservation.customers?.first_name} {reservation.customers?.last_name}
            {establishment?.name && ` chez ${establishment.name}`}
          </p>
        </div>

        <Card className="mb-6 p-6">
          <div className="flex items-center gap-2 mb-4">
            <h3 className="text-lg font-semibold">Réservation actuelle</h3>
          </div>
          <div className="grid gap-4 md:grid-cols-3 bg-muted/50 p-4 rounded-lg">
            <div className="flex items-center gap-2">
              <CalendarIcon className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm">
                {format(new Date(reservation.date), "dd/MM/yyyy", { locale: fr })}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm">{reservation.start_time.substring(0, 5)}</span>
            </div>
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm">
                {(reservation.number_of_people || 0) + (reservation.number_of_children || 0)} personnes
                {reservation.number_of_children > 0 && (
                  <span className="text-muted-foreground"> dont {reservation.number_of_children} enfant{reservation.number_of_children > 1 ? 's' : ''}</span>
                )}
              </span>
            </div>
          </div>
        </Card>

        {groupLocked ? (

            <Card className="p-6 text-center">
              <Users className="w-10 h-10 text-primary mx-auto mb-3" />
              <h3 className="text-lg font-semibold mb-2">Modification par le restaurant</h3>
              <p className="text-muted-foreground mb-4">{GROUP_MODIFICATION_MESSAGE}</p>
              <div className="space-y-2 text-sm">
                {phone && (
                  <div className="flex items-center justify-center gap-2">
                    <Phone className="w-4 h-4 text-muted-foreground" />
                    <a href={`tel:${phone}`} className="text-primary hover:underline">{phone}</a>
                  </div>
                )}
                {email && (
                  <div className="flex items-center justify-center gap-2">
                    <Mail className="w-4 h-4 text-muted-foreground" />
                    <a href={`mailto:${email}`} className="text-primary hover:underline">{email}</a>
                  </div>
                )}
              </div>
            </Card>
        ) : (
        <div className="space-y-6">
          {/* Date Selection */}
          <Card className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <CalendarIcon className="w-5 h-5 text-primary" />
              <Label className="text-lg font-semibold">Choisissez une nouvelle date</Label>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              Vous pouvez modifier votre réservation jusqu'à {reservationSettings?.minBookingDays ?? 2} jours à l'avance
            </p>
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={setSelectedDate}
              locale={fr}
              weekStartsOn={1}
              disabled={(date) => {
                const today = startOfDay(new Date());
                const minDays = reservationSettings?.minBookingDays ?? 2;
                const maxDays = reservationSettings?.maxBookingDays ?? 30;
                const minDate = addDays(today, minDays);
                const maxDate = addDays(today, maxDays);
                if (isBefore(date, minDate) || isAfter(date, maxDate)) return true;
                // Block establishment closure dates
                const { inClosure } = isDateInClosure(date);
                return inClosure;
              }}
              onDayClick={(date) => {
                const { inClosure, customerMessage } = isDateInClosure(date);
                if (inClosure) {
                  toast({
                    title: "Date indisponible",
                    description: <span className="whitespace-pre-line">{customerMessage || "Établissement fermé à cette date"}</span>,
                    variant: "destructive",
                  });
                }
              }}
              className="rounded-md border w-fit mx-auto"
            />
          </Card>

          {/* Note optionnelle de l'établissement (information seule, jamais bloquante ici) */}
          {selectedDate && applicableNotice && (
            <EstablishmentNoticeAlert notice={applicableNotice} idPrefix="modify" />
          )}

          {/* Terrace preference - shown right after date */}
          {selectedDate && reservationSettings?.terraceEnabled && (
            <Card className="p-6">
              <TerracePreferenceSelector
                value={terracePreference}
                 onChange={changeTerracePreference}
                helpText={reservationSettings?.terraceHelpText}
                hideNoPreference
                availability={placementAvailability}
              />
            </Card>
          )}

          {/* Zone preference - shown right after terrace */}
          {selectedDate && zonesUiEnabled && (
            <Card className="p-6">
              <ZonePreferenceSelector
                zones={activeZones}
                value={zonePreference}
                 onChange={changeZonePreference}
                mode={reservationSettings?.zonesSelectionMode ?? 'single'}
                helpText={reservationSettings?.zonesHelpText}
                allowNoPreference={!(reservationSettings?.zonesStrict ?? true) || (reservationSettings?.zonesSelectionMode ?? 'single') === 'multi'}
                availability={placementAvailability}
              />
            </Card>
          )}

          {/* Number of Persons and Time Slot Selection */}
          {selectedDate && terraceChoiceMade && (
            <div className="grid md:grid-cols-2 gap-6">
              {/* Number of Persons Selection */}
              <Card className="p-6">
                <Label className="text-lg font-semibold mb-4 block">Nombre de personnes</Label>
                <p className="text-sm text-muted-foreground mb-4">
                  {selectedSlot 
                    ? `Places disponibles pour le créneau ${selectedSlot}` 
                    : "Modifiez le nombre de personnes si nécessaire"}
                </p>
                <div className="space-y-3">
                  {availablePersonCounts.filter(p => p.isAvailable).length > 0 ? (
                    <>
                      {availablePersonCounts.filter(p => p.isAvailable).length <= 10 ? (
                        <div className="grid grid-cols-5 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-2">
                          {availablePersonCounts
                            .filter(({ isAvailable }) => isAvailable)
                            .map(({ count }) => (
                            <button
                              key={count}
                              type="button"
                              onClick={() => setPersons(count.toString())}
                              className={`p-4 rounded-lg border-2 font-semibold text-lg transition-all ${
                                persons === count.toString()
                                  ? "border-primary bg-primary text-primary-foreground shadow-md"
                                  : "border-border hover:border-primary/50 hover:bg-accent"
                              }`}
                            >
                              {count}
                            </button>
                          ))}
                        </div>
                      ) : (
                        <Select value={persons} onValueChange={setPersons}>
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Nombre de personnes" />
                          </SelectTrigger>
                          <SelectContent>
                            {availablePersonCounts
                              .filter(({ isAvailable }) => isAvailable)
                              .map(({ count }) => (
                              <SelectItem 
                                key={count} 
                                value={count.toString()}
                              >
                                {count} personne{count > 1 ? "s" : ""}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </>
                  ) : (
                    <div className="text-sm bg-red-50 dark:bg-red-950/30 text-red-600 dark:text-red-400 p-4 rounded-lg font-medium">
                      😔 Désolé, aucune table n'est disponible pour cette date.
                      <br />
                      <span className="text-xs mt-2 block">Essayez une autre date ou contactez-nous directement.</span>
                    </div>
                  )}
                </div>
              </Card>

              {/* Children selector - only show if children are enabled */}
              {reservationSettings?.childrenEnabled && (
                <Card className="p-6">
                  <Label className="text-lg font-semibold mb-4 block flex items-center gap-2">
                    <Baby className="w-5 h-5 text-primary" />
                    Dont combien d'enfants ?
                  </Label>
                  <p className="text-sm text-muted-foreground mb-4">
                    {reservationSettings?.childrenHelpText || 'Indiquez combien de personnes dans votre groupe sont des enfants.'}
                  </p>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => setNumberOfChildren(Math.max(0, numberOfChildren - 1))}
                      disabled={numberOfChildren <= 0}
                      className="h-10 w-10 rounded-lg border-2 border-border font-semibold text-lg hover:border-primary/50 disabled:opacity-50"
                    >
                      −
                    </button>
                    <span className="w-12 text-center font-semibold text-xl">{numberOfChildren}</span>
                    <button
                      type="button"
                      onClick={() => setNumberOfChildren(Math.min(Math.max((parseInt(persons) || 1) - 1, 0), numberOfChildren + 1))}
                      disabled={numberOfChildren >= Math.max((parseInt(persons) || 1) - 1, 0)}
                      className="h-10 w-10 rounded-lg border-2 border-border font-semibold text-lg hover:border-primary/50 disabled:opacity-50"
                    >
                      +
                    </button>
                    <span className="text-sm text-muted-foreground">enfant{numberOfChildren !== 1 ? 's' : ''}</span>
                  </div>
                </Card>
              )}

              {/* Stroller checkbox */}
              {reservationSettings?.strollerEnabled && (
                <Card className="p-4">
                  <div className="flex items-center gap-3">
                    <Checkbox
                      id="modifyHasStroller"
                      checked={hasStroller}
                      onCheckedChange={(checked) => setHasStroller(checked === true)}
                    />
                    <Label htmlFor="modifyHasStroller" className="text-sm font-normal cursor-pointer flex items-center gap-1.5">
                      <StrollerIcon size={14} /> {reservationSettings?.strollerHelpText || 'Cochez si vous venez avec une poussette.'}
                    </Label>
                  </div>
                </Card>
              )}

              {/* Time Slot / Time Selection */}
              <Card className="p-6">
                <Label className="text-lg font-semibold mb-4 block">
                  {bookingMode === 'time' ? "Heure de réservation" : "Créneau horaire"}
                </Label>
                <PlacementSelectionSummary
                  terracePreference={terracePreference}
                  terraceEnabled={reservationSettings?.terraceEnabled ?? false}
                  unavailable={selectedPlacementUnavailable}
                />
{!selectedPlacementUnavailable && (
                <p className="text-sm text-muted-foreground mb-4">
                  {persons 
                    ? bookingMode === 'time'
                      ? `Heures disponibles pour ${persons} personne${persons !== "1" ? "s" : ""}`
                      : `Créneaux pour ${persons} personne${persons !== "1" ? "s" : ""}` 
                    : bookingMode === 'time' ? "Choisissez une heure" : "Choisissez un créneau"}
                </p>
)}
                
                {selectedPlacementUnavailable ? null : bookingMode === 'time' ? (
                  /* TIME MODE: Use TimeBookingGrid */
                  <TimeBookingGrid
                    availableTimes={timeAvailability}
                    selectedTime={selectedSlot}
                    onTimeSelect={setSelectedSlot}
                    disabled={!persons}
                    closureMessage={slotClosureMessage}
                  />
                ) : (
                  /* SLOT MODE: Use traditional slot buttons */
                  <div className="space-y-2">
                    {filteredTimeSlots.length === 0 ? (
                      <p className="text-sm text-muted-foreground whitespace-pre-line">
                        {slotClosureMessage || 'Aucun créneau disponible pour cette combinaison.'}
                      </p>
                    ) : (
                      filteredTimeSlots.map((slot) => (
                        <button
                          key={slot.time}
                          type="button"
                          onClick={() => slot.hasAvailability && setSelectedSlot(slot.time)}
                          disabled={!slot.hasAvailability}
                          className={`w-full p-3 rounded-lg border-2 text-left transition-all ${
                            selectedSlot === slot.time
                              ? "border-primary bg-primary/10 shadow-md"
                              : slot.hasAvailability
                              ? "border-border hover:border-primary/50 hover:bg-accent"
                              : "border-border bg-muted opacity-60 cursor-not-allowed"
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <span className="font-medium">{slot.time}</span>
                            <div className={`text-xs px-2 py-1 rounded ${
                              slot.unavailableReason === 'full'
                                ? "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
                                : slot.unavailableReason === 'capacity'
                                ? "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
                                : "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                            }`}>
                              {slot.unavailableReason === 'full' 
                                ? "Complet" 
                                : slot.unavailableReason === 'capacity'
                                ? "Indisponible"
                                : "Disponible"}
                            </div>
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </Card>
            </div>
          )}

          {/* Marketing Consent Checkboxes */}
          {selectedDate && persons && selectedSlot && (reservationSettings?.emailMarketingEnabled || reservationSettings?.smsMarketingEnabled) && (
            <Card className="p-6">
              <div className="space-y-3">
                {reservationSettings?.emailMarketingEnabled && (
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="modifyMarketingConsent"
                      checked={marketingConsent}
                      onCheckedChange={(checked) => setMarketingConsent(checked === true)}
                      className="mt-0.5"
                    />
                    <Label htmlFor="modifyMarketingConsent" className="text-sm font-normal cursor-pointer leading-relaxed">
                      📧 Je souhaite recevoir des communications par e-mail de la part de l'établissement
                    </Label>
                  </div>
                )}
                {reservationSettings?.smsMarketingEnabled && (
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="modifySmsConsent"
                      checked={smsConsent}
                      onCheckedChange={(checked) => setSmsConsent(checked === true)}
                      className="mt-0.5"
                    />
                    <Label htmlFor="modifySmsConsent" className="text-sm font-normal cursor-pointer leading-relaxed">
                      📱 Je souhaite recevoir des communications par SMS de la part de l'établissement
                    </Label>
                  </div>
                )}
              </div>
            </Card>
          )}

          {/* Submit Button */}
          {selectedDate && persons && selectedSlot && (
            <Card className="p-6">
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  variant="outline"
                  onClick={() => navigate(backPath)}
                  className="flex-1"
                >
                  Annuler
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="flex-1"
                >
                  {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Confirmer la modification
                </Button>
              </div>
            </Card>
          )}
        </div>
        )}
      </div>
    </div>
  );
}
